/**
 * Bundle Manager Component
 * For creators to create and manage preset bundles
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  DollarSign,
  Tag,
  Music,
  Download,
  Star,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Loader2,
  Percent,
  ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Preset Selection Modal
const PresetSelectionModal = ({ 
  isOpen, 
  onClose, 
  availablePresets, 
  selectedIds, 
  onConfirm 
}) => {
  const [localSelected, setLocalSelected] = useState(new Set(selectedIds));
  
  useEffect(() => {
    setLocalSelected(new Set(selectedIds));
  }, [selectedIds]);
  
  if (!isOpen) return null;
  
  const togglePreset = (id) => {
    const newSet = new Set(localSelected);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setLocalSelected(newSet);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
      >
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-semibold text-white">Select Presets for Bundle</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 10rem)' }}>
          <p className="text-sm text-zinc-400 mb-4">
            Select 3-15 shared presets to include in your bundle. 
            <span className="text-amber-400"> Only shared presets are available.</span>
          </p>
          
          {availablePresets.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400">No shared presets available</p>
              <p className="text-sm text-zinc-500">Share some presets first to create a bundle</p>
            </div>
          ) : (
            <div className="space-y-2">
              {availablePresets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => togglePreset(preset.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    localSelected.has(preset.id)
                      ? 'bg-violet-500/20 border border-violet-500/50'
                      : 'bg-zinc-800/50 border border-transparent hover:border-zinc-700'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                    localSelected.has(preset.id)
                      ? 'bg-violet-500 border-violet-500'
                      : 'border-zinc-600'
                  }`}>
                    {localSelected.has(preset.id) && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-lg">{preset.icon || '💾'}</span>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-white">{preset.name}</p>
                    <p className="text-xs text-zinc-500">{preset.category}</p>
                  </div>
                  <span className="text-xs text-zinc-500">
                    ${(preset.price || 0).toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
          <span className="text-sm text-zinc-400">
            {localSelected.size} selected (3-15 required)
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => onConfirm([...localSelected])}
              disabled={localSelected.size < 3 || localSelected.size > 15}
              className="bg-violet-600 hover:bg-violet-500"
            >
              Confirm Selection
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Bundle Edit Form
const BundleEditForm = ({ 
  bundle, 
  availablePresets,
  onSave, 
  onCancel, 
  isLoading 
}) => {
  const [name, setName] = useState(bundle?.name || '');
  const [description, setDescription] = useState(bundle?.description || '');
  const [originalPrice, setOriginalPrice] = useState(bundle?.original_price || 0);
  const [bundlePrice, setBundlePrice] = useState(bundle?.bundle_price || 0);
  const [selectedPresetIds, setSelectedPresetIds] = useState(bundle?.preset_ids || []);
  const [tags, setTags] = useState((bundle?.tags || []).join(', '));
  const [showPresetModal, setShowPresetModal] = useState(false);
  
  const discountPercent = originalPrice > 0 
    ? Math.round(((originalPrice - bundlePrice) / originalPrice) * 100)
    : 0;
  
  const handleSubmit = () => {
    if (!name.trim()) {
      alert('Please enter a bundle name');
      return;
    }
    if (selectedPresetIds.length < 3) {
      alert('Please select at least 3 presets');
      return;
    }
    if (bundlePrice < 0) {
      alert('Bundle price cannot be negative');
      return;
    }
    
    onSave({
      name: name.trim(),
      description: description.trim(),
      original_price: parseFloat(originalPrice) || 0,
      bundle_price: parseFloat(bundlePrice) || 0,
      preset_ids: selectedPresetIds,
      tags: tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
    });
  };
  
  const selectedPresets = availablePresets.filter(p => selectedPresetIds.includes(p.id));
  
  return (
    <div className="space-y-4">
      {/* Bundle Name */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Bundle Name</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Rock Essentials Pack"
          className="bg-zinc-800/50 border-zinc-700"
        />
      </div>
      
      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe what makes this bundle special..."
          rows={3}
          className="w-full px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 resize-none"
        />
      </div>
      
      {/* Preset Selection */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Included Presets</label>
        <Button
          variant="outline"
          onClick={() => setShowPresetModal(true)}
          className="w-full justify-between border-zinc-700"
        >
          <span className="flex items-center gap-2">
            <Music className="w-4 h-4" />
            {selectedPresetIds.length} presets selected
          </span>
          <ChevronRight className="w-4 h-4" />
        </Button>
        
        {selectedPresets.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {selectedPresets.slice(0, 5).map((preset) => (
              <span key={preset.id} className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-300">
                {preset.icon} {preset.name}
              </span>
            ))}
            {selectedPresets.length > 5 && (
              <span className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-500">
                +{selectedPresets.length - 5} more
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Pricing */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-1.5">
            Original Value ($)
          </label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={originalPrice}
            onChange={(e) => setOriginalPrice(e.target.value)}
            className="bg-zinc-800/50 border-zinc-700"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-1.5">
            Bundle Price ($)
          </label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={bundlePrice}
            onChange={(e) => setBundlePrice(e.target.value)}
            className="bg-zinc-800/50 border-zinc-700"
          />
        </div>
      </div>
      
      {/* Discount Display */}
      {discountPercent > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 rounded-lg">
          <Percent className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-emerald-400">
            {discountPercent}% discount for buyers
          </span>
        </div>
      )}
      
      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">
          Tags (comma separated)
        </label>
        <Input
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="rock, metal, heavy, distortion"
          className="bg-zinc-800/50 border-zinc-700"
        />
      </div>
      
      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={isLoading}
          className="flex-1 bg-violet-600 hover:bg-violet-500"
        >
          {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {bundle ? 'Update Bundle' : 'Create Bundle'}
        </Button>
      </div>
      
      {/* Preset Selection Modal */}
      <PresetSelectionModal
        isOpen={showPresetModal}
        onClose={() => setShowPresetModal(false)}
        availablePresets={availablePresets}
        selectedIds={selectedPresetIds}
        onConfirm={(ids) => {
          setSelectedPresetIds(ids);
          setShowPresetModal(false);
          
          // Auto-calculate original price from selected presets
          const total = ids.reduce((sum, id) => {
            const preset = availablePresets.find(p => p.id === id);
            return sum + (preset?.price || 0);
          }, 0);
          setOriginalPrice(total.toFixed(2));
        }}
      />
    </div>
  );
};

// Bundle Card for management
const BundleManageCard = ({ bundle, onEdit, onDelete, onToggleActive }) => (
  <div className={`p-4 bg-zinc-900/50 border rounded-xl ${bundle.is_active ? 'border-zinc-800' : 'border-zinc-800/50 opacity-60'}`}>
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
        <Package className="w-6 h-6 text-white" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-white truncate">{bundle.name}</h3>
          {!bundle.is_active && (
            <span className="px-2 py-0.5 bg-zinc-700 text-zinc-400 text-xs rounded">Hidden</span>
          )}
        </div>
        <p className="text-sm text-zinc-400 line-clamp-1">{bundle.description}</p>
        
        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <Music className="w-3 h-3" />
            {bundle.preset_count} presets
          </span>
          <span className="flex items-center gap-1">
            <Download className="w-3 h-3" />
            {bundle.downloads} sales
          </span>
          <span className="flex items-center gap-1">
            <Star className="w-3 h-3 text-amber-400" />
            {bundle.rating?.toFixed(1) || '0.0'}
          </span>
          <span className="flex items-center gap-1 text-emerald-400">
            <DollarSign className="w-3 h-3" />
            ${bundle.bundle_price.toFixed(2)}
          </span>
        </div>
      </div>
      
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onToggleActive(bundle)}
          title={bundle.is_active ? 'Hide bundle' : 'Show bundle'}
        >
          {bundle.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(bundle)}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(bundle)}
          className="text-red-400 hover:text-red-300"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </div>
);

// Main Bundle Manager Component
export const BundleManager = ({ userId }) => {
  const [bundles, setBundles] = useState([]);
  const [availablePresets, setAvailablePresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Fetch user's bundles
  const fetchBundles = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/preset-bundles/my-bundles?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setBundles(data.bundles || []);
      }
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
    }
  }, [userId]);
  
  // Fetch user's shared presets
  const fetchAvailablePresets = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/guitar-presets/?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        // Filter to only shared presets
        const shared = (data.presets || []).filter(p => p.isShared);
        setAvailablePresets(shared);
      }
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);
  
  useEffect(() => {
    fetchBundles();
    fetchAvailablePresets();
  }, [fetchBundles, fetchAvailablePresets]);
  
  // Create or update bundle
  const handleSaveBundle = async (bundleData) => {
    setIsSaving(true);
    try {
      const url = editingBundle
        ? `${API_URL}/api/preset-bundles/${editingBundle.id}`
        : `${API_URL}/api/preset-bundles/`;
      
      const method = editingBundle ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, ...bundleData })
      });
      
      if (response.ok) {
        fetchBundles();
        setIsEditing(false);
        setEditingBundle(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save bundle');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save bundle');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Delete bundle
  const handleDeleteBundle = async (bundle) => {
    if (!confirm(`Are you sure you want to delete "${bundle.name}"?`)) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/preset-bundles/${bundle.id}?user_id=${userId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        fetchBundles();
      } else {
        alert('Failed to delete bundle');
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };
  
  // Toggle bundle active status
  const handleToggleActive = async (bundle) => {
    try {
      const response = await fetch(`${API_URL}/api/preset-bundles/${bundle.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          is_active: !bundle.is_active
        })
      });
      
      if (response.ok) {
        fetchBundles();
      }
    } catch (error) {
      console.error('Toggle error:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">My Bundles</h2>
            <p className="text-sm text-zinc-400">{bundles.length} bundles created</p>
          </div>
        </div>
        
        {!isEditing && (
          <Button
            onClick={() => {
              setEditingBundle(null);
              setIsEditing(true);
            }}
            className="bg-violet-600 hover:bg-violet-500"
            disabled={availablePresets.length < 3}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Bundle
          </Button>
        )}
      </div>
      
      {/* Warning if not enough presets */}
      {availablePresets.length < 3 && (
        <div className="flex items-center gap-3 p-4 bg-amber-500/20 border border-amber-500/30 rounded-xl">
          <AlertCircle className="w-5 h-5 text-amber-400" />
          <div>
            <p className="text-sm text-amber-300 font-medium">Not enough shared presets</p>
            <p className="text-xs text-amber-400/80">
              You need at least 3 shared presets to create a bundle. Share some presets first!
            </p>
          </div>
        </div>
      )}
      
      {/* Edit Form */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4 overflow-hidden"
          >
            <h3 className="font-semibold text-white mb-4">
              {editingBundle ? 'Edit Bundle' : 'Create New Bundle'}
            </h3>
            <BundleEditForm
              bundle={editingBundle}
              availablePresets={availablePresets}
              onSave={handleSaveBundle}
              onCancel={() => {
                setIsEditing(false);
                setEditingBundle(null);
              }}
              isLoading={isSaving}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Bundles List */}
      {bundles.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">No bundles yet</p>
          <p className="text-sm text-zinc-500">Create your first preset bundle to start selling</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bundles.map((bundle) => (
            <BundleManageCard
              key={bundle.id}
              bundle={bundle}
              onEdit={(b) => {
                setEditingBundle(b);
                setIsEditing(true);
              }}
              onDelete={handleDeleteBundle}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BundleManager;
